import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Inventory as InventoryIcon,
  OpenInNew as OpenIcon,
} from "@mui/icons-material";
import { useMutation, useQuery } from "@tanstack/react-query";

import { usePageTitle } from "../context/PageTitleContext";
import {
  OpeningInventoryPreviewResponse,
  OpeningInventorySignal,
  OpeningInventoryStrategy,
  openingInventoryService,
} from "../services/openingInventoryService";

const STRATEGY_OPTIONS: Array<{
  value: OpeningInventoryStrategy;
  label: string;
  helper: string;
}> = [
  {
    value: "conservative",
    label: "Conservadora",
    helper: "Menos SKUs, top rotación y menor riesgo operativo.",
  },
  {
    value: "balanced",
    label: "Balanceada",
    helper: "Mezcla demanda reciente, inventario vivo, distribuciones y capacidad.",
  },
  {
    value: "broad",
    label: "Amplia",
    helper: "Mayor cobertura de categorías sin rebasar monto ni capacidad.",
  },
];

const SIGNAL_LABEL: Record<OpeningInventorySignal, string> = {
  sales: "Ventas",
  inventory: "Inventario vivo",
  distribution: "Distribución reciente",
  margin: "Margen",
  opening: "Benchmark apertura",
};

export function InventarioAperturaPage(): React.ReactElement {
  const { setTitle } = usePageTitle();
  const navigate = useNavigate();
  const [store, setStore] = useState("");
  const [amount, setAmount] = useState("600000");
  const [strategy, setStrategy] = useState<OpeningInventoryStrategy>("balanced");
  const [targetMonths, setTargetMonths] = useState("2");
  const [capacityOverride, setCapacityOverride] = useState("");
  const [result, setResult] =
    useState<OpeningInventoryPreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTitle("Distribución — Inventario de apertura");
    return () => setTitle("");
  }, [setTitle]);

  const normalizedStore = store.trim().toUpperCase();
  const storeIsValid = /^[A-Z0-9_-]{1,20}$/.test(normalizedStore);
  const selectedStrategy = useMemo(
    () => STRATEGY_OPTIONS.find((item) => item.value === strategy)!,
    [strategy],
  );

  const contextQuery = useQuery({
    queryKey: ["opening-inventory-context", normalizedStore],
    queryFn: () => openingInventoryService.getContext(normalizedStore),
    enabled: storeIsValid,
    retry: false,
  });

  const createPreview = useMutation({
    mutationFn: () => {
      const input: Parameters<typeof openingInventoryService.createPreview>[0] = {
        store: normalizedStore,
        target_amount: Number(amount),
        strategy,
        target_months: Number(targetMonths),
      };
      const override = positiveNumberOrUndefined(capacityOverride);
      if (override) input.capacity_override_units = override;
      return openingInventoryService.createPreview(input);
    },
    onSuccess: (data) => {
      setResult(data);
      setError(null);
    },
    onError: (err: unknown) => {
      setResult(null);
      setError(extractErrorMessage(err));
    },
  });

  const amountNumber = Number(amount);
  const monthsNumber = Number(targetMonths);
  const canSubmit =
    storeIsValid &&
    Number.isFinite(amountNumber) &&
    amountNumber > 0 &&
    Number.isFinite(monthsNumber) &&
    monthsNumber > 0 &&
    !createPreview.isPending;

  function submit(): void {
    setError(null);
    createPreview.mutate();
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: "100%", overflowX: "hidden" }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <InventoryIcon color="primary" />
          <Typography variant="h4" component="h1" fontWeight={600}>
            Inventario de apertura
          </Typography>
        </Stack>

        <Paper variant="outlined" sx={{ p: 2, overflow: "hidden" }}>
          <Stack spacing={2}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                  xl: "repeat(5, minmax(0, 1fr))",
                },
                gap: 2,
                alignItems: "start",
                minWidth: 0,
              }}
            >
              <TextField
                label="Tienda destino"
                value={store}
                onChange={(event) => setStore(event.target.value.toUpperCase())}
                inputProps={{ maxLength: 20 }}
                placeholder="SLP07"
                size="small"
                sx={{ minWidth: 0 }}
              />
              <TextField
                label="Monto objetivo al costo"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                type="number"
                size="small"
                inputProps={{ min: 1, step: 1000 }}
                sx={{ minWidth: 0 }}
              />
              <TextField
                label="Estrategia de apertura"
                value={strategy}
                onChange={(event) =>
                  setStrategy(event.target.value as OpeningInventoryStrategy)
                }
                select
                size="small"
                sx={{ minWidth: 0 }}
              >
                {STRATEGY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Meses objetivo"
                value={targetMonths}
                onChange={(event) => setTargetMonths(event.target.value)}
                type="number"
                size="small"
                inputProps={{ min: 0.5, step: 0.5 }}
                sx={{ minWidth: 0 }}
              />
              <TextField
                label="Capacidad objetivo"
                value={capacityOverride}
                onChange={(event) => setCapacityOverride(event.target.value)}
                type="number"
                size="small"
                inputProps={{ min: 1, step: 100 }}
                placeholder="Opcional"
                sx={{ minWidth: 0 }}
              />
            </Box>

            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                disabled={!canSubmit}
                onClick={submit}
                startIcon={
                  createPreview.isPending ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : (
                    <InventoryIcon />
                  )
                }
                sx={{
                  minHeight: 40,
                  minWidth: { xs: "100%", sm: 180 },
                  whiteSpace: "nowrap",
                  px: 2,
                }}
              >
                Generar pedido
              </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {selectedStrategy.helper} Basado en ventas recientes, inventario
              vivo de tiendas comparables, distribuciones recientes y benchmark
              SLP04/SLP06.
            </Typography>

            {storeIsValid && (
              <ContextPanel
                isLoading={contextQuery.isLoading}
                error={contextQuery.error}
                data={contextQuery.data}
              />
            )}
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {result && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Pedido de validación generado
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {result.summary.store} · {result.summary.request_id}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<OpenIcon />}
                  onClick={() =>
                    navigate(
                      `/compras/distribucion/preview/${result.preview.session.id}`,
                    )
                  }
                >
                  Abrir revisión
                </Button>
              </Stack>

              <Divider />

              <Stack direction="row" spacing={4} flexWrap="wrap">
                <Stat label="Costo estimado" value={formatCurrency(result.summary.estimated_cost)} />
                <Stat label="Costo con IVA" value={formatCurrency(result.summary.estimated_cost_iva)} />
                <Stat label="SKUs" value={formatNumber(result.summary.selected_skus)} />
                <Stat label="Piezas" value={formatNumber(result.summary.selected_units)} />
                <Stat label="Capacidad" value={formatNumber(result.summary.capacity_units)} />
                <Stat label="Tamaño" value={result.summary.store_size} />
              </Stack>

              <Alert severity="info" variant="outlined">
                La revisión previa puede excluir líneas antes de autorizar. Las
                transferencias hacia Odoo se crean únicamente al aprobar esa
                revisión.
              </Alert>

              <SelectedLinesTable lines={result.summary.selected_lines.slice(0, 12)} />
            </Stack>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}

function ContextPanel({
  isLoading,
  error,
  data,
}: {
  isLoading: boolean;
  error: unknown;
  data: Awaited<ReturnType<typeof openingInventoryService.getContext>> | undefined;
}): React.ReactElement | null {
  if (isLoading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Cargando capacidad y comparables...
        </Typography>
      </Stack>
    );
  }
  if (error) {
    return <Alert severity="warning">{extractErrorMessage(error)}</Alert>;
  }
  if (!data) return null;
  const chipSx = {
    maxWidth: "100%",
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  };
  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ minWidth: 0 }}>
      <Chip label={`Tamaño: ${data.store_size}`} size="small" sx={chipSx} />
      <Chip
        label={`Capacidad: ${formatNumber(data.capacity_units)} pzas`}
        size="small"
        sx={chipSx}
      />
      <Chip
        label={`Comparables: ${data.comparable_stores.slice(0, 5).join(", ")}`}
        size="small"
        sx={chipSx}
      />
      <Chip
        label={`SLP04/SLP06 mediana: ${formatNumber(data.benchmark.small_store_units_median)} pzas`}
        size="small"
        sx={chipSx}
      />
    </Stack>
  );
}

function SelectedLinesTable({
  lines,
}: {
  lines: OpeningInventoryPreviewResponse["summary"]["selected_lines"];
}): React.ReactElement {
  if (lines.length === 0) return <></>;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" aria-label="líneas seleccionadas">
        <TableHead>
          <TableRow>
            <TableCell>Producto</TableCell>
            <TableCell align="right">Piezas</TableCell>
            <TableCell align="right">Costo</TableCell>
            <TableCell align="right">Score</TableCell>
            <TableCell>Señal dominante</TableCell>
            <TableCell>Origen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.unified_product_id}>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  {line.product_code ?? line.unified_product_id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {line.product_name}
                </Typography>
              </TableCell>
              <TableCell align="right">{formatNumber(line.qty)}</TableCell>
              <TableCell align="right">{formatCurrency(line.estimated_cost)}</TableCell>
              <TableCell align="right">{line.score.toFixed(1)}</TableCell>
              <TableCell>{SIGNAL_LABEL[line.dominant_signal]}</TableCell>
              <TableCell>{line.origin_warehouse}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function Stat({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <Box sx={{ minWidth: 120 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={600}>
        {value}
      </Typography>
    </Box>
  );
}

function positiveNumberOrUndefined(value: string): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function formatCurrency(value: number): string {
  return value.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  });
}

function formatNumber(value: number): string {
  return value.toLocaleString("es-MX", { maximumFractionDigits: 0 });
}

function extractErrorMessage(err: unknown): string {
  const candidate = err as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };
  return (
    candidate.response?.data?.error ??
    candidate.response?.data?.message ??
    candidate.message ??
    "No se pudo generar el inventario de apertura"
  );
}
